import { Skeleton, Stack, Title } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { getPerformanceModelName } from "./utils";

import { PerformanceChart } from "@/components/elements";
import { useMarketCar } from "@/services/market/get-market-car";
import { useMarketCarValuation } from "@/services/market/get-market-car-valuation";
import { tooltipFormatter } from "@/utils/tooltipFormatter";

import type { DatesRange } from "@/types/entity";
import type { PerformanceChart as IPerformanceChart } from "@/types/performance";
import type { FC } from "react";

export type ModelCompareValuationProps = {
  car1Id: string;
  car2Id: string;
  car3Id?: string;
};

export const ModelCompareValuation: FC<ModelCompareValuationProps> = ({
  car1Id,
  car2Id,
  car3Id,
}) => {
  const { t } = useTranslation();
  const [range, setRange] = useState<DatesRange>();
  const { data: car1Data, isLoading: car1IsLoading } = useMarketCar({ carId: car1Id });
  const { data: car2Data, isLoading: car2IsLoading } = useMarketCar({ carId: car2Id });
  const { data: car3Data, isLoading: car3IsLoading } = useMarketCar({ carId: car3Id ?? "" });

  const { data: car1Valuation, isLoading: car1ValuationIsLoading } = useMarketCarValuation({
    carId: car1Id,
    ...range,
  });
  const { data: car2Valuation, isLoading: car2ValuationIsLoading } = useMarketCarValuation({
    carId: car2Id,
    ...range,
  });
  const { data: car3Valuation, isLoading: car3ValuationIsLoading } = useMarketCarValuation({
    carId: car3Id,
    ...range,
  });

  const isLoading =
    car1IsLoading ||
    car2IsLoading ||
    car1ValuationIsLoading ||
    car2ValuationIsLoading ||
    (Boolean(car3Id) && (car3IsLoading || car3ValuationIsLoading));

  const series1 = car1Valuation?.performance?.data || [];
  const series2 = car2Valuation?.performance?.data || [];
  const series3 = car3Valuation?.performance?.data || [];

  const targetSeries = [series1, series2, series3].reduce(
    (longest, current) => (current.length > longest.length ? current : longest),
    []
  );

  const pad = (data: typeof series1) => {
    if (targetSeries.length > data.length) {
      return [
        ...targetSeries
          .slice(0, targetSeries.length - data.length)
          .map((point) => ({ ...point, value: 0 })),
        ...data,
      ];
    }
    return data;
  };

  const data1 = pad(series1);
  const data2 = pad(series2);
  const data3 = pad(series3);

  const chartData1: IPerformanceChart = {
    name: getPerformanceModelName(car1Data),
    data: data1,
  };
  const chartData2: IPerformanceChart = {
    name: getPerformanceModelName(car2Data),
    data: data2,
  };
  const chartData3: IPerformanceChart = {
    name: getPerformanceModelName(car3Data),
    data: data3,
  };

  return (
    <Stack>
      <Title order={3}>{t("car.valuation")}</Title>
      <Skeleton visible={isLoading}>
        <PerformanceChart
          benchmarks={[chartData2, ...(car3Id ? [chartData3] : [])]}
          performance={chartData1}
          loading={isLoading}
          onTimeframeChange={setRange}
          defaultBenchmark="__allCars__"
          compareMode
          showPerformanceWhenBenchmarkSelected={false}
          tooltipFormatter={tooltipFormatter}
          restrictKpisByPlan
          showTooltip={false}
          showConditionsTooltip={false}
        />
      </Skeleton>
    </Stack>
  );
};
